import { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

interface Mensaje {
  id: number;
  texto: string;
  fecha_inicio: string | null;
  fecha_fin: string | null;
  activo: boolean;
  orden: number;
}

interface Config {
  velocidad: number;
  solo_programados: boolean;
}

const hoyISO = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

const esProgramado = (m: Mensaje) => !!(m.fecha_inicio || m.fecha_fin);

const estadoProgramado = (m: Mensaje): 'vigente' | 'proximo' | 'vencido' => {
  const hoy = hoyISO();
  if (m.fecha_inicio && m.fecha_inicio > hoy) return 'proximo';
  if (m.fecha_fin && m.fecha_fin < hoy) return 'vencido';
  return 'vigente';
};

const fmt = (f: string | null) => (f ? f.split('-').reverse().join('/') : '—');

export default function BannerTexto() {
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const [mensajes, setMensajes] = useState<Mensaje[]>([]);
  const [config, setConfig]     = useState<Config>({ velocidad: 20, solo_programados: true });
  const [cargando, setCargando] = useState(true);
  const [error, setError]       = useState('');
  const [ok, setOk]             = useState('');
  const [nuevo, setNuevo]       = useState({ texto: '', fecha_inicio: '', fecha_fin: '' });
  const [editId, setEditId]     = useState<number | null>(null);
  const [edit, setEdit]         = useState({ texto: '', fecha_inicio: '', fecha_fin: '' });

  const cargar = async () => {
    try {
      const r = await axios.get(`${API}/ticker/admin`, { headers });
      setMensajes(r.data.mensajes);
      setConfig(r.data.config);
    } catch {
      setError('No se pudo cargar la información');
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aviso = (m: string) => { setOk(m); setError(''); setTimeout(() => setOk(''), 2500); };
  const fallo = (e: any, m: string) => {
    const d = e?.response?.data?.detail;
    setError(typeof d === 'string' ? d : m);
  };

  const cuerpo = (m: Mensaje) => ({
    texto: m.texto, fecha_inicio: m.fecha_inicio, fecha_fin: m.fecha_fin, activo: m.activo, orden: m.orden,
  });

  const guardarConfig = async () => {
    try {
      const r = await axios.put(`${API}/ticker/config`, config, { headers });
      setConfig(r.data);
      aviso('Configuración guardada');
    } catch (e) { fallo(e, 'Error al guardar la configuración'); }
  };

  const agregar = async () => {
    if (!nuevo.texto.trim()) { setError('Escriba un texto'); return; }
    const orden = mensajes.length ? Math.max(...mensajes.map(m => m.orden)) + 1 : 0;
    try {
      await axios.post(`${API}/ticker/mensajes`, {
        texto: nuevo.texto.trim(),
        fecha_inicio: nuevo.fecha_inicio || null,
        fecha_fin: nuevo.fecha_fin || null,
        activo: true,
        orden,
      }, { headers });
      setNuevo({ texto: '', fecha_inicio: '', fecha_fin: '' });
      aviso('Mensaje agregado');
      cargar();
    } catch (e) { fallo(e, 'Error al agregar'); }
  };

  const empezarEdicion = (m: Mensaje) => {
    setEditId(m.id);
    setEdit({ texto: m.texto, fecha_inicio: m.fecha_inicio || '', fecha_fin: m.fecha_fin || '' });
  };

  const guardarEdicion = async (m: Mensaje) => {
    try {
      await axios.put(`${API}/ticker/mensajes/${m.id}`, {
        ...cuerpo(m),
        texto: edit.texto.trim(),
        fecha_inicio: edit.fecha_inicio || null,
        fecha_fin: edit.fecha_fin || null,
      }, { headers });
      setEditId(null);
      aviso('Cambios guardados');
      cargar();
    } catch (e) { fallo(e, 'Error al guardar'); }
  };

  const alternar = async (m: Mensaje) => {
    try {
      await axios.put(`${API}/ticker/mensajes/${m.id}`, { ...cuerpo(m), activo: !m.activo }, { headers });
      cargar();
    } catch (e) { fallo(e, 'Error al actualizar'); }
  };

  const eliminar = async (m: Mensaje) => {
    if (!window.confirm(`¿Eliminar "${m.texto}"?`)) return;
    try {
      await axios.delete(`${API}/ticker/mensajes/${m.id}`, { headers });
      aviso('Eliminado');
      cargar();
    } catch (e) { fallo(e, 'Error al eliminar'); }
  };

  const mover = async (lista: Mensaje[], idx: number, dir: -1 | 1) => {
    const destino = idx + dir;
    if (destino < 0 || destino >= lista.length) return;
    const nueva = [...lista];
    [nueva[idx], nueva[destino]] = [nueva[destino], nueva[idx]];
    try {
      await Promise.all(nueva.map((m, i) =>
        m.orden === i ? null : axios.put(`${API}/ticker/mensajes/${m.id}`, { ...cuerpo(m), orden: i }, { headers })
      ));
      cargar();
    } catch (e) { fallo(e, 'Error al reordenar'); }
  };

  // Lo que se ve hoy en el sitio
  const ordenados  = [...mensajes].sort((a, b) => a.orden - b.orden || a.id - b.id);
  const activos    = ordenados.filter(m => m.activo);
  const progHoy    = activos.filter(m => esProgramado(m) && estadoProgramado(m) === 'vigente');
  const fijosAct   = activos.filter(m => !esProgramado(m));
  const enVivo     = progHoy.length && config.solo_programados ? progHoy : [...progHoy, ...fijosAct];

  const fijos       = ordenados.filter(m => !esProgramado(m));
  const programados = ordenados.filter(m => esProgramado(m))
    .sort((a, b) => (a.fecha_inicio || '').localeCompare(b.fecha_inicio || ''));

  const fila = (m: Mensaje, lista: Mensaje[], idx: number, conOrden: boolean) => {
    const editando = editId === m.id;
    const est = esProgramado(m) ? estadoProgramado(m) : null;
    return (
      <div key={m.id}
        className={`flex flex-col md:flex-row md:items-center gap-3 p-4 rounded-xl border ${
          m.activo ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200 opacity-60'}`}>
        {conOrden && (
          <div className="flex md:flex-col gap-1">
            <button onClick={() => mover(lista, idx, -1)} disabled={idx === 0}
              className="px-2 text-gray-400 hover:text-azul disabled:opacity-30">▲</button>
            <button onClick={() => mover(lista, idx, 1)} disabled={idx === lista.length - 1}
              className="px-2 text-gray-400 hover:text-azul disabled:opacity-30">▼</button>
          </div>
        )}

        <div className="flex-1 min-w-0">
          {editando ? (
            <div className="space-y-2">
              <input value={edit.texto} onChange={e => setEdit({ ...edit, texto: e.target.value })}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
              <div className="flex flex-wrap gap-2 items-center text-sm">
                <span className="text-gray-500">Desde</span>
                <input type="date" value={edit.fecha_inicio}
                  onChange={e => setEdit({ ...edit, fecha_inicio: e.target.value })}
                  className="border border-gray-300 rounded-lg px-2 py-1" />
                <span className="text-gray-500">Hasta</span>
                <input type="date" value={edit.fecha_fin}
                  onChange={e => setEdit({ ...edit, fecha_fin: e.target.value })}
                  className="border border-gray-300 rounded-lg px-2 py-1" />
                <button onClick={() => setEdit({ ...edit, fecha_inicio: '', fecha_fin: '' })}
                  className="text-xs text-gray-400 underline">Quitar fechas (volver fijo)</button>
              </div>
            </div>
          ) : (
            <>
              <div className="font-medium text-gray-800 truncate">{m.texto}</div>
              {est && (
                <div className="flex flex-wrap items-center gap-2 mt-1 text-xs">
                  <span className="text-gray-500">📅 {fmt(m.fecha_inicio)} → {fmt(m.fecha_fin)}</span>
                  {est === 'vigente' && <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-bold">Vigente hoy</span>}
                  {est === 'proximo' && <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-bold">Próximo</span>}
                  {est === 'vencido' && <span className="bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-bold">Vencido</span>}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {editando ? (
            <>
              <button onClick={() => guardarEdicion(m)}
                className="bg-rojo text-white text-xs font-bold px-4 py-2 rounded-lg">Guardar</button>
              <button onClick={() => setEditId(null)}
                className="bg-gray-100 text-gray-600 text-xs font-bold px-4 py-2 rounded-lg">Cancelar</button>
            </>
          ) : (
            <>
              <button onClick={() => empezarEdicion(m)}
                className="bg-gray-100 text-gray-700 text-xs font-bold px-3 py-2 rounded-lg hover:bg-gray-200">✏️ Editar</button>
              <button onClick={() => alternar(m)}
                className={`text-xs font-bold px-3 py-2 rounded-lg ${m.activo ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'}`}>
                {m.activo ? '● Activo' : '○ Inactivo'}
              </button>
              <button onClick={() => eliminar(m)}
                className="bg-red-50 text-red-600 text-xs font-bold px-3 py-2 rounded-lg hover:bg-red-100">🗑️</button>
            </>
          )}
        </div>
      </div>
    );
  };

  if (cargando) return <div className="text-gray-500">Cargando...</div>;

  return (
    <div className="max-w-5xl mx-auto">
      <style>{`@keyframes tickerPreview { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>

      <h1 className="text-2xl font-bold text-azul mb-1">📢 Banner de texto</h1>
      <p className="text-gray-500 text-sm mb-6">
        Mensajes de la franja roja del inicio. Los mensajes <strong>sin fechas</strong> se muestran siempre;
        los mensajes <strong>con fechas</strong> quedan guardados y aparecen solos en su rango.
      </p>

      {error && <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-xl mb-4">{error}</div>}
      {ok && <div className="bg-green-50 text-green-700 text-sm px-4 py-3 rounded-xl mb-4">✓ {ok}</div>}

      {/* VISTA PREVIA */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="font-bold text-gray-800 mb-3">👁️ Así se ve hoy en el sitio</h2>
        <div className="bg-rojo py-3 overflow-hidden rounded-xl">
          {enVivo.length ? (
            <div style={{ display: 'flex', animation: `tickerPreview ${config.velocidad}s linear infinite`, whiteSpace: 'nowrap' }}>
              {enVivo.concat(enVivo).map((m, i) => (
                <span key={i} className="text-white text-xs font-bold tracking-wider flex-shrink-0" style={{ paddingLeft: '48px' }}>
                  {m.texto} <span style={{ marginLeft: '48px', opacity: 0.5 }}>◆</span>
                </span>
              ))}
            </div>
          ) : (
            <div className="text-white/80 text-xs text-center">No hay mensajes activos: el sitio mostrará los textos por defecto</div>
          )}
        </div>
      </div>

      {/* CONFIGURACIÓN */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="font-bold text-gray-800 mb-4">⚙️ Configuración</h2>
        <label className="block text-sm text-gray-600 mb-2">
          Velocidad: <strong className="text-azul">{config.velocidad} segundos</strong> por vuelta
        </label>
        <input type="range" min={5} max={120} value={config.velocidad}
          onChange={e => setConfig({ ...config, velocidad: Number(e.target.value) })}
          className="w-full" />
        <div className="flex justify-between text-xs text-gray-400 mb-4">
          <span>⚡ Más rápido</span><span>🐢 Más lento</span>
        </div>
        <label className="flex items-center gap-3 text-sm text-gray-700 mb-4 cursor-pointer">
          <input type="checkbox" checked={config.solo_programados}
            onChange={e => setConfig({ ...config, solo_programados: e.target.checked })} />
          Cuando haya un mensaje programado vigente, mostrar <strong>solo</strong> los programados (ocultar los fijos)
        </label>
        <button onClick={guardarConfig}
          className="bg-rojo text-white text-sm font-bold px-6 py-2 rounded-lg hover:opacity-90">Guardar configuración</button>
      </div>

      {/* NUEVO MENSAJE */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="font-bold text-gray-800 mb-4">➕ Nuevo mensaje</h2>
        <input value={nuevo.texto} onChange={e => setNuevo({ ...nuevo, texto: e.target.value })}
          placeholder="Ej: 🎄 ¡Feliz Navidad! 10% de descuento en baterías"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-3" />
        <div className="flex flex-wrap gap-3 items-center text-sm mb-2">
          <span className="text-gray-500">Desde</span>
          <input type="date" value={nuevo.fecha_inicio}
            onChange={e => setNuevo({ ...nuevo, fecha_inicio: e.target.value })}
            className="border border-gray-300 rounded-lg px-2 py-1" />
          <span className="text-gray-500">Hasta</span>
          <input type="date" value={nuevo.fecha_fin}
            onChange={e => setNuevo({ ...nuevo, fecha_fin: e.target.value })}
            className="border border-gray-300 rounded-lg px-2 py-1" />
          <button onClick={agregar}
            className="bg-rojo text-white text-sm font-bold px-6 py-2 rounded-lg hover:opacity-90 ml-auto">Agregar</button>
        </div>
        <p className="text-xs text-gray-400">Deje las fechas vacías para un mensaje fijo. Con fechas, queda programado.</p>
      </div>

      {/* FIJOS */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="font-bold text-gray-800 mb-4">📌 Mensajes fijos <span className="text-gray-400 font-normal text-sm">({fijos.length})</span></h2>
        <div className="space-y-2">
          {fijos.length ? fijos.map((m, i) => fila(m, fijos, i, true))
            : <p className="text-sm text-gray-400">No hay mensajes fijos.</p>}
        </div>
      </div>

      {/* PROGRAMADOS */}
      <div className="bg-white rounded-2xl shadow-sm p-6 mb-6">
        <h2 className="font-bold text-gray-800 mb-4">🗓️ Mensajes programados (memoria) <span className="text-gray-400 font-normal text-sm">({programados.length})</span></h2>
        <div className="space-y-2">
          {programados.length ? programados.map((m, i) => fila(m, programados, i, false))
            : <p className="text-sm text-gray-400">No hay mensajes programados. Agregue uno con fechas arriba.</p>}
        </div>
      </div>
    </div>
  );
}
